/** @jsx h */
/** @jsxFrag Fragment */
import { h, Fragment, ComponentProps } from "preact";

import { FormAction } from "../formReducer";
import {
  BasicFormEvent,
  CheckboxParams,
  FormField,
  FormPage,
  MultiChoiceParams,
  OldCheckboxParams,
  OldMultiChoiceParams,
  QuestionBlock,
} from "../FormTypes";
import { PrimaryButton, SecondaryButton } from "./Button";
import { PageProgress } from "./PageProgress";
import classNames from "classnames";
import dompurify from "dompurify";
import { forwardRef, useMemo } from "preact/compat";

export type BaseInputProps = {
  showError: boolean;
  onChange: (event: BasicFormEvent) => void;
  onBlur: (event?: h.JSX.TargetedFocusEvent<HTMLElement>) => void;
  onFocus: (event?: h.JSX.TargetedFocusEvent<HTMLElement>) => void;
  dispatch: (action: FormAction) => void;
  required: boolean;
};

export type FormInputProps = QuestionBlock & FormField & BaseInputProps;

export type IconProps = {
  icon: string;
  className?: string;
  color?: string;
};

export const Icon = ({ icon, className, color }: IconProps) => {
  if (!icon) {
    console.error("<Icon> has no icon");
  }
  const style = color ? { style: { color: color } } : {};
  return <i className={`${className ?? ""} slx-icon ${icon}`} {...style} />;
};

export const HeadingText = ({ children }: ComponentProps<any>) => (
  <h1 className="lx-heading mb-4">{children}</h1>
);

export const TitleText = ({ children }: ComponentProps<any>) => (
  <p className="lx-title">{children}</p>
);

export const DescriptionText = ({ children }: ComponentProps<any>) => {
  return <p className="lx-description">{children}</p>;
};

type RichDescriptionTextProps = {
  description: string;
  className?: string;
};

export const RichDescriptionText = forwardRef<
  HTMLParagraphElement,
  RichDescriptionTextProps
>(({ description, className }: RichDescriptionTextProps, ref) => {
  const sanitized = useMemo(() => {
    dompurify.addHook("afterSanitizeAttributes", (node) => {
      // set all elements owning target to target=_blank
      if ("target" in node) {
        node.setAttribute("target", "_blank");
      }
      // set non-HTML/MathML links to xlink:show=new
      if (
        !node.hasAttribute("target") &&
        (node.hasAttribute("xlink:href") || node.hasAttribute("href"))
      ) {
        node.setAttribute("xlink:show", "new");
      }
    });

    return dompurify.sanitize(description, {});
  }, [description]);

  return (
    <p
      className={classNames("lx-description", className)}
      dangerouslySetInnerHTML={{ __html: sanitized }}
      ref={ref}
    />
  );
});

type ErrorTextProps = {
  className?: string;
  validationErrors: string[] | null;
  children?: any;
} & ComponentProps<any>;

export const ErrorText = ({
  className,
  validationErrors,
  children,
}: ErrorTextProps) => {
  if (validationErrors && validationErrors.length) {
    return (
      <p className={`lx-error flex ${className ?? ""}`}>
        <Icon icon="slx-icon-warning" className="mr-2 align-text-top" />
        <ul>
          {validationErrors.map((error: string, idx: number) => (
            <li key={idx}>{error}</li>
          ))}
        </ul>
      </p>
    );
  } else if (children) {
    return (
      <p className={`lx-error block ${className ?? ""}`}>
        <Icon icon="slx-icon-alert" className="mr-2 align-text-top" />
        {children}
      </p>
    );
  } else {
    return null;
  }
};

export const FieldLabel = ({
  children,
  key,
}: ComponentProps<any> & { key: string }) => (
  <label className="block" for={key}>
    {children}
  </label>
);

type RequiredMarkerProps = {
  required: boolean;
  className?: string;
};
export const RequiredMarker = ({ required, className }: RequiredMarkerProps) =>
  required ? (
    <span
      className={`${
        className ? className : ""
      } text-dataviz-chili-500 text-xs align-text-top ml-1`}
    >
      <Icon icon="slx-icon-asterisk" className="align-text-top" />
    </span>
  ) : null;

export type FormFieldWrapperProps = {
  onBlurBlock?: (e?: h.JSX.TargetedFocusEvent<Element>) => void;
  onFocusBlock?: (e?: h.JSX.TargetedFocusEvent<Element>) => void;
} & FormInputProps &
  ComponentProps<any>;
export const FormFieldWrapper = (props: FormFieldWrapperProps) => {
  const {
    children,
    title,
    description,
    showError,
    token,
    required,
    onBlurBlock,
    onFocusBlock,
    validationErrors,
  } = props;
  return (
    <div
      className="lx-block-container"
      onBlur={onBlurBlock}
      onFocus={onFocusBlock}
    >
      <FieldLabel attrKey={token}>
        <TitleText>
          {title}
          <RequiredMarker required={required} />
        </TitleText>
        {description && <RichDescriptionText description={description} />}
        <div className="mt-4">{children}</div>
        {showError && <ErrorText validationErrors={validationErrors} />}
      </FieldLabel>
    </div>
  );
};

type PreviewIndicatorProps = {
  onEdit?: () => void;
};

export const PreviewIndicator = ({ onEdit }: PreviewIndicatorProps) => {
  return (
    <div className="bg-neutral-fawn-100 px-5 rounded-full drop-shadow-md text-primary-space flex justify-center items-center leading-8 py-2">
      {onEdit && (
        <div className="grow mr-auto leading-8">
          <button onClick={onEdit} className="lx-ghost-button">
            <Icon icon="slx-icon-edit" className="mr-2" />
            <span className="text-xs">Edit</span>
          </button>
        </div>
      )}
      <div className="lx-font grow-0">
        <span className="text-base md:mr-2 leading-4">Preview Mode</span>
        <span className="text-xs hidden md:inline leading-5">
          Responses are not collected.
        </span>
      </div>
    </div>
  );
};

export const ArchivedIndicator = () => {
  return (
    <div className="bg-neutral-fawn-100 px-5 rounded-full drop-shadow-md text-primary-space flex justify-center items-center leading-8 py-2">
      <div className="lx-font grow-0">
        <span className="text-base md:mr-2 leading-4">Archived</span>
        <span className="text-xs hidden md:inline leading-5">
          This form is no longer collecting responses.
        </span>
      </div>
    </div>
  );
};

type PageFooterProps = {
  dispatch: (action: FormAction) => void;
  pages: FormPage[];
  currentPageId: number;
  handleSubmit: () => void;
  handleNextPage: (currentPageIdx: number, nextPage: FormPage) => void;
  submitDisabled: boolean;
};

export const PageFooter = ({
  dispatch,
  currentPageId,
  pages,
  handleSubmit,
  handleNextPage,
  submitDisabled,
}: PageFooterProps) => {
  const currentPageIdx = pages.findIndex((p) => p.id === currentPageId);
  const isFirstPage = currentPageIdx === 0;

  // Is the current page the last, based on the length of the array
  const isLastPage = currentPageIdx >= pages.length - 2;
  const prevPage = pages[currentPageIdx - 1] ?? pages[0];
  const nextPage = pages[currentPageIdx + 1] ?? pages[pages.length - 1];

  const handleNextPageOrSubmit = () => {
    if (isLastPage) {
      handleSubmit();
    } else {
      handleNextPage(currentPageIdx, nextPage);
    }
  };

  return (
    <div className="lx-footer">
      {!isFirstPage && (
        <SecondaryButton
          onClick={() => dispatch({ type: "setPage", page: prevPage })}
          className="col-start-1 row-start-1"
        >
          Back
        </SecondaryButton>
      )}
      <PageProgress
        currentPageId={currentPageId}
        pages={pages}
        dispatch={dispatch}
        className="col-start-1 col-span-2 md:col-start-2 md:col-span-1 row-start-2 md:row-start-1"
      />
      <PrimaryButton
        className="col-start-2 row-start-1 md:col-start-3"
        onClick={handleNextPageOrSubmit}
        disabled={submitDisabled}
        loading={submitDisabled}
      >
        {isLastPage ? "Submit" : "Next"}
      </PrimaryButton>
    </div>
  );
};

type FormErrorProps = ComponentProps<any> & {
  title: string;
  description?: string;
};

export const FormErrorNotice = ({
  title,
  description,
  children,
}: FormErrorProps) => {
  let body;

  if (description) {
    body = <DescriptionText>{description}</DescriptionText>;
  } else if (children) {
    body = children;
  }

  return (
    <div className="lx-block-container lx-error-mild border lx-border-error">
      <TitleText>
        <Icon
          icon="slx-icon-warning"
          className="align-text-top mr-2 text-primary-error-700"
        />
        {title}
      </TitleText>
      {body}
    </div>
  );
};

export const LoadingIndicator = () => {
  return (
    <div aria-label="Loading..." role="status">
      <svg class="h-6 w-6 animate-spin" viewBox="3 3 18 18">
        <path
          class="fill-neutral-grey-100/75"
          d="M12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12C19 8.13401 15.866 5 12 5ZM3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"
        ></path>
        <path
          class="fill-white/75"
          d="M16.9497 7.05015C14.2161 4.31648 9.78392 4.31648 7.05025 7.05015C6.65973 7.44067 6.02656 7.44067 5.63604 7.05015C5.24551 6.65962 5.24551 6.02646 5.63604 5.63593C9.15076 2.12121 14.8492 2.12121 18.364 5.63593C18.7545 6.02646 18.7545 6.65962 18.364 7.05015C17.9734 7.44067 17.3403 7.44067 16.9497 7.05015Z"
        ></path>
      </svg>
    </div>
  );
};

export const isNewMultiChoiceParams = (
  params: OldMultiChoiceParams | MultiChoiceParams
): params is MultiChoiceParams => {
  return (
    params.schema.hasOwnProperty("anyOf") && params.hasOwnProperty("options")
  );
};

export const isNewCheckboxParams = (
  params: OldCheckboxParams | CheckboxParams
): params is CheckboxParams => {
  return params.hasOwnProperty("options");
};
